import { UIEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { InboxContactChatLinks, InboxContactChatMessageLinks } from 'src/constants/types';
import { AssetCardAsset } from 'src/modules/assetLibrary/components/AssetCard/AssetCard';
import { useInboxApi } from '../api';
import { MediaLibraryDialogProps } from '../types';

const PAGE_SIZE = 20;

export type MediaLibraryAsset = AssetCardAsset;

export const useMediaLibraryDialogController = ({
  open,
  companyId,
  contactId,
}: MediaLibraryDialogProps) => {
  const { getContactChatLinks: getInboxContactChatLinks } = useInboxApi();
  const [items, setItems] = useState<InboxContactChatMessageLinks[]>([]);
  const [count, setCount] = useState(0);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [previewAsset, setPreviewAsset] = useState<MediaLibraryAsset | null>(null);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const hasMore = useMemo(() => items.length < count, [items.length, count]);
  const mediaAssets = useMemo<MediaLibraryAsset[]>(
    () =>
      items.flatMap(item => {
        return (item.linksWithMimeType || []).map((link, linkIndex) => ({
          id: `${item.id}-${linkIndex}`,
          name: link.name,
          url: link.url,
          mimeType: link.mimeType || 'application/octet-stream',
        }));
      }),
    [items],
  );

  const fetchLinks = useCallback(
    async (offset: number, append: boolean) => {
      if (!companyId || !contactId) {
        return;
      }

      if (offset === 0) {
        setIsLoadingInitial(true);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const { data } = await getInboxContactChatLinks({
          companyId,
          contactId,
          limit: PAGE_SIZE,
          offset,
        });
        const result = data as InboxContactChatLinks;
        setCount(result.count);
        setItems(prevItems => (append ? prevItems.concat(result.items || []) : result.items || []));
      } catch (error) {
        console.error('Failed to fetch media library links', error);
        if (!append) {
          setItems([]);
          setCount(0);
        }
      } finally {
        setIsLoadingInitial(false);
        setIsLoadingMore(false);
      }
    },
    [companyId, contactId, getInboxContactChatLinks],
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    setItems([]);
    setCount(0);
    fetchLinks(0, false);
  }, [open, fetchLinks]);

  const handleLoadMore = useCallback(() => {
    if (isLoadingInitial || isLoadingMore || !hasMore) {
      return;
    }
    fetchLinks(items.length, true);
  }, [fetchLinks, hasMore, isLoadingInitial, isLoadingMore, items.length]);

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const isAtBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 24;
      if (isAtBottom) {
        handleLoadMore();
      }
    },
    [handleLoadMore],
  );

  const handleImageError = useCallback((assetId: string) => {
    setFailedImages(prev => new Set([...prev, assetId]));
  }, []);

  const closePreview = useCallback(() => {
    setPreviewAsset(null);
  }, []);

  return {
    isLoadingInitial,
    isLoadingMore,
    mediaAssets,
    failedImages,
    previewAsset,
    setPreviewAsset,
    handleScroll,
    handleImageError,
    closePreview,
  };
};
